import { createAsyncThunk } from '@reduxjs/toolkit';
import CompanyPermissionsService from '@/services/CompanyPermissionsService';
import { toAssigneeParams } from './assignees';

const handler = (name, asyncFunction) =>
  createAsyncThunk(`CompanyPermissions/${name}`, async (arg) => {
    const data = await asyncFunction(arg);
    return data;
  });

export const getCompanyPermissions = handler('getCompanyPermissions', () =>
  CompanyPermissionsService.getPermissions()
);

export const changeGlobalPermissions = handler(
  'changeGlobalPermissions',
  async (changes) => {
    // Each permission has its own request. Keep successful edits when another
    // request in the same batch fails.
    const results = await Promise.allSettled(
      changes.map(({ id, enabled }) =>
        CompanyPermissionsService.changeGlobalPermission({
          id,
          value: enabled ? 'yes' : 'no',
        })
      )
    );

    return changes.reduce(
      (batch, change, index) => {
        const key =
          results[index].status === 'fulfilled' ? 'succeeded' : 'failed';
        batch[key].push(change);
        return batch;
      },
      { succeeded: [], failed: [] }
    );
  }
);

export const getPermissionGroups = handler('getPermissionGroups', () =>
  CompanyPermissionsService.getPermissionGroups()
);

export const createPermissionGroup = createAsyncThunk(
  'CompanyPermissions/createPermissionGroup',
  async (permissionGroup, { rejectWithValue }) => {
    try {
      return await CompanyPermissionsService.createPermissionGroup(
        permissionGroup
      );
    } catch (error) {
      // ApiService attaches `status`/`response` to the error, but RTK's
      // default serializer strips them — carry them through rejectWithValue
      // so the caller can still read the backend's validation message.
      return rejectWithValue({
        status: error?.status,
        response: error?.response,
      });
    }
  }
);

export const getPermissionGroup = handler('getPermissionGroup', (id) =>
  CompanyPermissionsService.getPermissionGroup(id)
);

export const updatePermissionGroupName = handler(
  'updatePermissionGroupName',
  (permissionGroup) =>
    CompanyPermissionsService.updatePermissionGroupName(permissionGroup)
);

export const changeGroupPermissions = handler(
  'changeGroupPermissions',
  async ({ groupId, changes }) => {
    // Each permission has its own request, same as the global permissions
    // toggle, so a failure only reverts the permissions that failed.
    const results = await Promise.allSettled(
      changes.map(({ id, enabled }) =>
        CompanyPermissionsService.changeGroupPermission({
          groupId,
          id,
          value: enabled ? 'yes' : 'no',
        })
      )
    );

    return changes.reduce(
      (batch, change, index) => {
        const key =
          results[index].status === 'fulfilled' ? 'succeeded' : 'failed';
        batch[key].push(change);
        return batch;
      },
      { succeeded: [], failed: [] }
    );
  }
);

export const deletePermissionGroup = handler('deletePermissionGroup', (id) =>
  CompanyPermissionsService.deletePermissionGroup(id)
);

export const getAgentSupervision = handler('getAgentSupervision', () =>
  CompanyPermissionsService.getAgentSupervision()
);

export const getAgentSupervisionConfig = handler(
  'getAgentSupervisionConfig',
  () => CompanyPermissionsService.getAgentSupervisionConfig()
);

// Sends `savingRules`, which `pending` fills with the edited rules before this
// runs. The backend wants the lock on every write, so they go out with the
// lock as it is stored.
export const saveAgentSupervisionRules = createAsyncThunk(
  'CompanyPermissions/saveAgentSupervisionRules',
  async (_, { getState }) => {
    const { lock, savingRules } =
      getState().CompanyPermissionsSlice.agentSupervisionConfig;
    const response =
      await CompanyPermissionsService.updateAgentSupervisionConfig({
        lock,
        rules: savingRules,
      });

    return response?.rules;
  }
);

export const updateAgentSupervisionLock = handler(
  'updateAgentSupervisionLock',
  async (lock) => {
    const response =
      await CompanyPermissionsService.updateAgentSupervisionConfig({
        lock,
        rules: [],
      });

    return response?.lock ?? lock;
  }
);

export const getAgentSupervisionExemptions = handler(
  'getAgentSupervisionExemptions',
  () => CompanyPermissionsService.getAgentSupervisionExemptions()
);

// `params` is exactly one of user_id, company_group_id, branch_id or team_id.
export const addAgentSupervisionExemption = handler(
  'addAgentSupervisionExemption',
  (params) => CompanyPermissionsService.addAgentSupervisionExemption(params)
);

export const removeAgentSupervisionExemption = handler(
  'removeAgentSupervisionExemption',
  (params) => CompanyPermissionsService.removeAgentSupervisionExemption(params)
);

export const setExemptAdminsFromWarnings = handler(
  'setExemptAdminsFromWarnings',
  (exempt) =>
    CompanyPermissionsService.updateAgentSupervisionExemptAdmins({
      exempt_admins_from_warnings: exempt,
    })
);

export const getControlListingEdition = handler(
  'getControlListingEdition',
  () => CompanyPermissionsService.getControlListingEdition()
);

export const toggleAgentSupervision = handler(
  'toggleAgentSupervision',
  async ({ id, enabled }) => {
    const field = `${id}_enabled`;
    const response = await CompanyPermissionsService.updateAgentSupervision({
      [field]: enabled,
    });

    return { id, enabled: response?.[field] ?? enabled };
  }
);

export const setControlListingEditionActive = handler(
  'setControlListingEditionActive',
  async ({ id, enabled, pendingChangesPolicy }) => {
    // Defaulting the policy would silently approve or reject unreviewed work.
    if (!enabled && !pendingChangesPolicy) {
      throw new Error(
        'A pending changes policy is required to disable control listing edition'
      );
    }

    const response =
      await CompanyPermissionsService.updateControlListingEdition(
        enabled
          ? { active: true }
          : { active: false, pending_changes_policy: pendingChangesPolicy }
      );

    return { id, enabled: response?.active ?? enabled };
  }
);

export const getPortfolioManagers = handler('getPortfolioManagers', () =>
  CompanyPermissionsService.getControlListingEditionPortfolioManagers()
);

// Add and remove answer with the refreshed list, so there is no second fetch
// and no chance of the UI drifting from what the backend stored.
export const addPortfolioManager = handler('addPortfolioManager', (assignee) =>
  CompanyPermissionsService.addControlListingEditionPortfolioManager(
    toAssigneeParams(assignee)
  )
);

export const removePortfolioManager = handler(
  'removePortfolioManager',
  (assignee) =>
    CompanyPermissionsService.removeControlListingEditionPortfolioManager(
      toAssigneeParams(assignee)
    )
);

export const setPortfolioManagerBranches = handler(
  'setPortfolioManagerBranches',
  ({ assignee, branchIds }) =>
    CompanyPermissionsService.updateControlListingEditionPortfolioManagerBranches(
      { ...toAssigneeParams(assignee), branch_ids: branchIds }
    )
);

export const getUsersWithoutSupervision = handler(
  'getUsersWithoutSupervision',
  () =>
    CompanyPermissionsService.getControlListingEditionUsersWithoutSupervision()
);

export const addUserWithoutSupervision = handler(
  'addUserWithoutSupervision',
  (assignee) =>
    CompanyPermissionsService.addControlListingEditionUserWithoutSupervision(
      toAssigneeParams(assignee)
    )
);

export const removeUserWithoutSupervision = handler(
  'removeUserWithoutSupervision',
  (assignee) =>
    CompanyPermissionsService.removeControlListingEditionUserWithoutSupervision(
      toAssigneeParams(assignee)
    )
);

export const getSupervisionPolicy = handler(
  'getSupervisionPolicy',
  async () => {
    const response =
      await CompanyPermissionsService.getControlListingEditionSupervisionPolicy();

    return response?.listing_edition_supervision_policy;
  }
);

export const updateSupervisionPolicy = handler(
  'updateSupervisionPolicy',
  async (policy) => {
    const response =
      await CompanyPermissionsService.updateControlListingEditionSupervisionPolicy(
        { listing_edition_supervision_policy: policy }
      );

    return response?.listing_edition_supervision_policy ?? policy;
  }
);

export const getCompanyBranches = handler('getCompanyBranches', async () => {
  const response = await CompanyPermissionsService.getCompanyBranches();

  return (response?.branches || [])
    .map(({ id, name }) => ({ id, name: name || '' }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

export const getControlListingEditionPendingDisableWork = handler(
  'getControlListingEditionPendingDisableWork',
  async () => {
    const response =
      await CompanyPermissionsService.getControlListingEditionPendingDisableWork();

    return Boolean(response?.has_pending_disable_work);
  }
);
